import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useImmerReducer } from "use-immer";
import { errorMessage, successMessage } from "../../../utils";
import {
  clonePromosOrNotificationsApi,
  deletePromosOrNotificationsApi,
  getPromosOrNotificationsDetailsApi,
  publishOrUnpublishPromosOrNotificationsApi,
} from "../api/promosAndNotificationsApi";

const reducer = (draft, action) => {
  switch (action.type) {
    case "SET_DIALOG_OPEN":
      draft.isDialogsOpen[action.payload.type] = action.payload.open;
      break;
    case "SET_LOADING":
      draft.loading[action.payload.name] = action.payload.value;
      break;
    case "SET_DETAILS":
      const { start_date, end_date, stores, ...details } = action.payload;
      draft.details = details;
      draft.validityAndStoreDetails = { start_date, end_date, stores };
      break;
    case "SET_STATUS":
      draft.details.status = action.payload;
      break;
    default:
      break;
  }
};

const PROMO_NOTIFICATION_STATUS = {
  publish: "published",
  unpublish: "unpublished",
};

export const usePromosAndNotificationsDetails = ({ enableApi = true }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, dispatch] = useImmerReducer(reducer, {
    details: null,
    validityAndStoreDetails: null,
    isDialogsOpen: {
      clone: false,
      published: false,
    },
    loading: {
      list: true,
      deleteButton: false,
      playButton: false,
      pauseButton: false,
      cloneButton: false,
    },
  });

  const isEditMode = Boolean(id);
  const isNotification = state.details?.type === "notification";
  const title = state.details?.name;

  const breadcrumbLinks = React.useMemo(
    () => [
      {
        name: "Dashboard",
        href: "/",
      },
      {
        name: "Promos & Notifications",
        href: "/promos-and-notifications",
      },
      {
        name: title,
      },
    ],
    [title],
  );

  const getPromosOrNotificationsDetails = async (id) => {
    dispatch({ type: "SET_LOADING", payload: { name: "list", value: true } });
    try {
      const response = await getPromosOrNotificationsDetailsApi(id);
      const { success, message, data } = response;
      if (success) {
        dispatch({
          type: "SET_DETAILS",
          payload: data,
        });
        dispatch({ type: "SET_IS_FILTER_APPLIED", payload: false });
      } else {
        errorMessage(message);
      }
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch the details.";
      errorMessage(message);
    } finally {
      dispatch({
        type: "SET_LOADING",
        payload: { name: "list", value: false },
      });
    }
  };

  const deletePromosOrNotification = async () => {
    dispatch({
      type: "SET_LOADING",
      payload: { name: "deleteButton", value: true },
    });
    try {
      const response = await deletePromosOrNotificationsApi(id);
      const { success, message } = response;
      if (success) {
        successMessage(message);
        navigate("/promos-and-notifications");
      } else {
        errorMessage(message);
      }
    } catch (error) {
      const message =
        error.response?.data?.message ??
        `Failed to delete the ${state.details?.type}`;
      errorMessage(message);
    } finally {
      dispatch({
        type: "SET_LOADING",
        payload: { name: "deleteButton", value: false },
      });
    }
  };

  const clonePromosOrNotification = async () => {
    dispatch({
      type: "SET_LOADING",
      payload: { name: "cloneButton", value: true },
    });
    try {
      const response = await clonePromosOrNotificationsApi(id);
      const { success, message, data } = response;
      if (success) {
        successMessage(message);
        await getPromosOrNotificationsDetails(data?.id);
        navigate(`/promos-and-notifications/${data?.id}`);
      } else {
        errorMessage(message);
      }
    } catch (error) {
      const message =
        error.response?.data?.message ??
        `Failed to clone the ${state.details?.type}`;
      errorMessage(message);
    } finally {
      dispatch({
        type: "SET_LOADING",
        payload: { name: "cloneButton", value: false },
      });
    }
  };

  const publishOrUnpublishPromosOrNotification = async ({ type }) => {
    const key =
      type === PROMO_NOTIFICATION_STATUS.publish ? "playButton" : "pauseButton";
    dispatch({
      type: "SET_LOADING",
      payload: { name: key, value: true },
    });
    try {
      const response = await publishOrUnpublishPromosOrNotificationsApi({
        id,
        type,
      });
      const { success, message, data } = response;
      if (success) {
        successMessage(message);
        dispatch({ type: "SET_STATUS", payload: data.status });
      } else {
        errorMessage(message);
      }
    } catch (error) {
      const message =
        error.response?.data?.message ??
        `Failed to delete the ${state.details?.type}`;
      errorMessage(message);
    } finally {
      dispatch({
        type: "SET_LOADING",
        payload: { name: key, value: false },
      });
    }
  };

  const onCloneNotificationOrPromo = async () => {
    dispatch({
      type: "SET_DIALOG_OPEN",
      payload: { type: "clone", open: false },
    });
    await clonePromosOrNotification();
  };

  const onPublishNotificationOrPromo = async () => {
    dispatch({
      type: "SET_DIALOG_OPEN",
      payload: { type: "published", open: false },
    });
    await publishOrUnpublishPromosOrNotification({
      type: PROMO_NOTIFICATION_STATUS.publish,
    });
  };

  const onDelete = async () => {
    dispatch({
      type: "SET_DIALOG_OPEN",
      payload: { type: "delete", open: false },
    });
    await deletePromosOrNotification();
  };

  useEffect(() => {
    enableApi && getPromosOrNotificationsDetails(id);
  }, []);

  return {
    state,
    title,
    isNotification,
    isEditMode,
    breadcrumbLinks,
    PROMO_NOTIFICATION_STATUS,
    dispatch,
    onDelete,
    onPublishNotificationOrPromo,
    onCloneNotificationOrPromo,
    publishOrUnpublishPromosOrNotification,
  };
};
